import { errors } from './errors';
import { loginAfterUnauthorizedConnection } from './actions';
import { clearToken, logOut } from '@containers/App/actions';
import { setRedirectUrl } from '@containers/App/components/Redirector/actions';
import { ROUTES } from '@shared/constants';
import { getFromLocalStorage, LOCAL_STORAGE_ITEM } from '@utils/localStorageHelper';
import {
  isSharedVideoChatV2MeetingActive,
  queuePendingAuthAction,
  setMeetingPresenceInactiveListener,
} from '@services/meetingPresenceService';
import { getAppStore } from '@services/storeRegistry';

const PENDING_AUTH = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  PASSWORD_RESET_REQUIRED: 'PASSWORD_RESET_REQUIRED',
};

const PENDING_AUTH_PRIORITY = {
  [PENDING_AUTH.PASSWORD_RESET_REQUIRED]: 0,
  [PENDING_AUTH.UNAUTHORIZED]: 1,
};

let isReplayListenerInitialized = false;

const getOrderedPendingTypes = (pendingTypes = []) => {
  const uniqueTypes = Array.from(new Set(pendingTypes));
  return uniqueTypes.sort((a, b) => {
    const aPriority = Object.prototype.hasOwnProperty.call(PENDING_AUTH_PRIORITY, a)
      ? PENDING_AUTH_PRIORITY[a]
      : Number.MAX_SAFE_INTEGER;
    const bPriority = Object.prototype.hasOwnProperty.call(PENDING_AUTH_PRIORITY, b)
      ? PENDING_AUTH_PRIORITY[b]
      : Number.MAX_SAFE_INTEGER;
    return aPriority - bPriority;
  });
};

const buildPasswordResetActions = () => {
  const storedUser = getFromLocalStorage(LOCAL_STORAGE_ITEM.USER);
  const email = storedUser?.email;

  return [
    // Clear auth state synchronously so AuthenticationRoute allows ResetPassword.
    clearToken(),
    setRedirectUrl(ROUTES.RESET_PASSWORD, {
      reason: 'passwordResetRequired',
      ...(email ? { email } : {}),
    }),
    // Keep existing cleanup behavior (popups, Cognito sign-out best effort).
    logOut(),
  ];
};

const buildActionsForType = (type) => {
  if (type === PENDING_AUTH.UNAUTHORIZED) {
    return [loginAfterUnauthorizedConnection(errors.UNAUTHORIZED_CONNECTION_INVALID_TOKEN), logOut()];
  }

  if (type === PENDING_AUTH.PASSWORD_RESET_REQUIRED) {
    return buildPasswordResetActions();
  }

  return [];
};

export const getUnauthorizedActions = (errorMessage) => {
  const actions = [loginAfterUnauthorizedConnection(errorMessage)];

  if (isSharedVideoChatV2MeetingActive()) {
    queuePendingAuthAction(PENDING_AUTH.UNAUTHORIZED);
    return actions;
  }

  actions.push(logOut());
  return actions;
};

export const getPasswordResetRequiredActions = () => {
  if (isSharedVideoChatV2MeetingActive()) {
    // Do not interrupt active meeting flow with forced auth reset.
    queuePendingAuthAction(PENDING_AUTH.PASSWORD_RESET_REQUIRED);
    return [];
  }

  return buildPasswordResetActions();
};

export const initPendingAuthReplay = () => {
  if (isReplayListenerInitialized) {
    return;
  }

  setMeetingPresenceInactiveListener((pendingTypes = []) => {
    if (!Array.isArray(pendingTypes) || pendingTypes.length === 0) {
      return;
    }

    const orderedPendingTypes = getOrderedPendingTypes(pendingTypes);
    const store = getAppStore();

    if (!store?.dispatch) {
      orderedPendingTypes.forEach((type) => {
        queuePendingAuthAction(type);
      });
      return;
    }

    orderedPendingTypes.forEach((type) => {
      const actions = buildActionsForType(type);
      actions.forEach((action) => store.dispatch(action));
    });
  });

  isReplayListenerInitialized = true;
};
